import { EmailOutlined as Email, LockOutlined as Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { type Token, useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import { validatePassword } from "../../utils/utilFunctions";
import { SigninLayout, SubmitButton } from "./common";

class ValidationError extends Error {}

interface ResetPasswordParams {
	token: string;
	newPassword: string;
	confirmPassword: string;
	username?: string;
	setupId?: number;
	fromPendingSetup?: boolean;
}

function useResetPassword({
	token,
	newPassword,
	confirmPassword,
	username,
	setupId,
	fromPendingSetup,
}: ResetPasswordParams): UseMutationResult<Token | undefined, Error, void, unknown> {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { saveTokenData } = useAuth();
	const navigate = useNavigate();

	const createValidationMessage = useCallback(
		(errors: string[]): string => {
			return errors.map((error) => t(error)).join("\n  ");
		},
		[t],
	);

	return useMutation<Token | undefined, Error, void>({
		mutationKey: ["useResetPassword", token, newPassword],
		mutationFn: async () => {
			try {
				track("reset_password", {
					action: "submit_clicked",
					password_length: newPassword.length,
					confirm_password_length: confirmPassword.length,
					passwords_match: newPassword === confirmPassword,
					from_pending_setup: fromPendingSetup,
				});

				// Validate required fields based on flow
				if (fromPendingSetup) {
					if (!username || !setupId) {
						trackError("reset_password", { message: "Username or setup ID missing for pending setup flow", code: "validation_error" });
						throw new ValidationError(t("reset.password.error"));
					}
				} else {
					if (!token) {
						trackError("reset_password", { message: "Token missing for regular reset flow", code: "validation_error" });
						throw new ValidationError(t("reset.password.error"));
					}
				}

				const passValidation = validatePassword(newPassword);
				if (passValidation.is_valid === false) {
					trackError("reset_password", { message: passValidation.errors.join(", "), code: "validation_error" });
					throw new ValidationError(createValidationMessage(passValidation.errors));
				}

				if (newPassword !== confirmPassword) {
					trackError("reset_password", { message: "Passwords do not match", code: "passwords_mismatch", error_type: "passwords_mismatch" });
					throw new ValidationError(t("passwords.do.not.match"));
				}

				let tokenData: Token | undefined;
				if (fromPendingSetup && username && setupId) {
					await backendService.resetPasswordFromApp({ newPassword, username });
				} else {
					const { data } = await backendService.resetPassword({ token, newPassword });
					tokenData = data;
				}

				showSnackbar(t("reset.password.success"), "success");
				track("reset_password", { status: "success", from_pending_setup: fromPendingSetup });

				if (tokenData?.access_token) {
					await saveTokenData(tokenData);
					track("login_after_reset_password", { status: "success" });
					navigate("/");
				} else {
					navigate("/login");
				}

				return tokenData;
			} catch (error) {
				if (error instanceof ValidationError) {
					showSnackbar(error.message, "error");
					return;
				}

				let message = t("reset.password.error");
				if (error instanceof AxiosError) {
					message = error.response?.data?.detail || message;
				} else if (error instanceof Error) {
					message = error.message;
				}

				trackError("reset_password", { action: "api_failed", error_source: "api", message });
				showSnackbar(message, "error");
			}
		},
	});
}

interface LocationState {
	token?: string;
	username?: string;
	setupId?: number;
	setup?: boolean;
	welcome?: boolean;
	fromPendingSetup?: boolean;
}

export const ResetPassword: FC = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const location = useLocation();

	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const [searchParams, _] = useSearchParams();

	// Get values from navigation state (from pending setup flow) or URL params
	const locationState = location.state as LocationState | null;
	const token = locationState?.token || searchParams.get("token") || "";
	const initialUsername = locationState?.username || searchParams.get("username") || "";
	const setupId = locationState?.setupId;
	const fromPendingSetup = locationState?.fromPendingSetup || false;
	const isSetup = locationState?.setup || locationState?.welcome || searchParams.get("welcome") === "True" || searchParams.get("setup") === "True";

	const [username, setUsername] = useState(initialUsername);

	useEffect(() => {
		// Only validate token if not from pending setup flow
		if (!token && !fromPendingSetup) {
			trackError("reset_password", {
				action: "page init",
				message: "empty token",
			});
			showSnackbar(t("the.token.is.missing.please.use.another.link.to.reset.your.password"), "error", 20_000);
		}
	}, [trackError, showSnackbar, token, fromPendingSetup, t]);

	const handleNewPasswordChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setNewPassword(e.target.value);
			track("reset_password", {
				action: "field_changed",
				field: "new_password",
				length: e.target.value?.length,
				has_value: !!e.target.value,
			});
		},
		[track],
	);

	const handleConfirmPasswordChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setConfirmPassword(e.target.value);
			track("reset_password", {
				action: "field_changed",
				field: "confirm_password",
				length: e.target.value?.length,
				has_value: !!e.target.value,
			});
		},
		[track],
	);

	const handleToggleNewPasswordVisibility = useCallback(() => {
		setShowNewPassword((show) => !show);
	}, []);
	useEffect(() => {
		track("reset_password", {
			action: "visibility_toggled",
			field: "new_password",
			is_visible: showNewPassword,
		});
	}, [track, showNewPassword]);

	const handleToggleConfirmPasswordVisibility = useCallback(() => {
		setShowConfirmPassword((show) => !show);
	}, []);
	useEffect(() => {
		track("reset_password", {
			action: "visibility_toggled",
			field: "new_password",
			is_visible: showConfirmPassword,
		});
	}, [track, showConfirmPassword]);

	const { mutateAsync: resetPassword } = useResetPassword({
		token,
		newPassword,
		confirmPassword,
		username,
		setupId,
		fromPendingSetup,
	});
	const handleSubmit = async () => {
		await resetPassword();
	};

	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	return (
		<SigninLayout title={isSetup ? t("setup.password") : t("reset.password")}>
			<Box component="form" sx={{ display: "contents" }} action={handleSubmit}>
				{/* Show username field when coming from pending setup flow */}
				{fromPendingSetup && (
					<TextField
						value={username}
						onChange={(e) => setUsername(e.target.value)}
						placeholder={t("username")}
						variant="outlined"
						size="small"
						name="username"
						required
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Email sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
							},
						}}
						sx={{ mb: 1 }}
					/>
				)}
				<TextField
					ref={inputRef}
					type={showNewPassword ? "text" : "password"}
					value={newPassword}
					onChange={handleNewPasswordChange}
					placeholder={t("password")}
					variant="outlined"
					size="small"
					name="newPassword"
					required
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Lock />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={handleToggleNewPasswordVisibility}>{showNewPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>
				<TextField
					type={showConfirmPassword ? "text" : "password"}
					value={confirmPassword}
					onChange={handleConfirmPasswordChange}
					placeholder={t("password.confirmation")}
					variant="outlined"
					size="small"
					name="confirmPassword"
					required
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Lock />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={handleToggleConfirmPasswordVisibility}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>
				<SubmitButton disabled={(!token && !username) || !newPassword || !confirmPassword}>{isSetup ? t("setup.password") : t("reset.password")}</SubmitButton>
			</Box>
		</SigninLayout>
	);
};
